import { getRequiredSlots } from '../gphSkillRegistry';

const GAME_ALIASES = {
  pubg: 'pubg_mobile',
  pubgm: 'pubg_mobile',
  'pubg mobile': 'pubg_mobile',
  freefire: 'free_fire',
  'free fire': 'free_fire',
  ff: 'free_fire',
  cod: 'codm',
  codm: 'codm',
  'cod mobile': 'codm',
  'call of duty mobile': 'codm',
  'call of duty: mobile': 'codm',
  perfil: 'profile',
  profile: 'profile',
  'meu perfil': 'profile',
  'perfil salvo': 'profile'
};

const INTENT_ALIASES = {
  recommend: 'recommend_training',
  recommendation: 'recommend_training',
  recommend_training: 'recommend_training',
  treino: 'recommend_training',
  drill: 'recommend_training',
  continue: 'continue_track',
  continuar: 'continue_track',
  continue_track: 'continue_track',
  open: 'open_training',
  abrir: 'open_training',
  open_training: 'open_training',
  complete: 'complete_training',
  concluir: 'complete_training',
  complete_training: 'complete_training',
  save: 'save_training',
  salvar: 'save_training',
  save_training: 'save_training',
  progress: 'show_progress',
  progresso: 'show_progress',
  show_progress: 'show_progress'
};

const SUPPORTED_GAME_OR_PROFILE = new Set(['pubg_mobile', 'free_fire', 'codm', 'profile']);
const SUPPORTED_INTENTS = new Set([
  'recommend_training',
  'continue_track',
  'open_training',
  'complete_training',
  'save_training',
  'show_progress'
]);

const stripAccents = (value = '') =>
  String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const cleanToken = (value = '') => stripAccents(value).replace(/[^a-z0-9:_]+/g, ' ').trim();

const compactText = (value, max = 160) => String(value || '').trim().slice(0, max);

const normalizeGameOrProfile = (value) => {
  const raw = compactText(value, 80);
  if (!raw) return 'profile';
  const cleaned = cleanToken(raw);
  return GAME_ALIASES[cleaned] || raw;
};

const normalizeTrainingIntent = (value) => {
  const raw = compactText(value, 80);
  if (!raw) return '';
  const cleaned = cleanToken(raw);
  return INTENT_ALIASES[cleaned] || raw;
};

const getPainOrObjective = (slots = {}) => compactText(
  slots.painOrObjective || slots.pain || slots.objective || slots.drillGoal || slots.query,
  220
);

const requiredForIntent = (intent) => {
  const required = getRequiredSlots('coach_training', intent);
  return Array.isArray(required) ? required : [];
};

export const normalizeCoachTrainingSlots = (slots = {}) => {
  const normalized = { ...slots };
  normalized.trainingIntent = normalizeTrainingIntent(
    normalized.trainingIntent || normalized.intent || normalized.progressAction
  );
  normalized.gameOrProfile = normalizeGameOrProfile(
    normalized.gameOrProfile || normalized.game || normalized.gameId || normalized.profile
  );
  normalized.painOrObjective = getPainOrObjective(normalized);
  normalized.trainingId = compactText(normalized.trainingId || normalized.id, 120);
  normalized.trackId = compactText(normalized.trackId, 120);
  normalized.progressAction = compactText(normalized.progressAction, 60);
  return normalized;
};

export const validateCoachTrainingSlots = (slots = {}) => {
  const normalizedSlots = normalizeCoachTrainingSlots(slots);
  const missingSlots = [];
  const invalidSlots = [];
  const intent = normalizedSlots.trainingIntent;

  if (!intent) {
    missingSlots.push('trainingIntent');
  } else if (!SUPPORTED_INTENTS.has(intent)) {
    invalidSlots.push({ slot: 'trainingIntent', value: intent, reason: 'unsupported_training_intent' });
  }

  if (
    normalizedSlots.gameOrProfile
    && !SUPPORTED_GAME_OR_PROFILE.has(normalizedSlots.gameOrProfile)
  ) {
    invalidSlots.push({ slot: 'gameOrProfile', value: normalizedSlots.gameOrProfile, reason: 'unsupported_game' });
  }

  if (intent && SUPPORTED_INTENTS.has(intent)) {
    requiredForIntent(intent).forEach((slot) => {
      if (slot === 'gameOrProfile' && !normalizedSlots.gameOrProfile) missingSlots.push(slot);
      if (slot === 'painOrObjective' && !normalizedSlots.painOrObjective) missingSlots.push(slot);
      if (slot === 'trainingId' && !normalizedSlots.trainingId) missingSlots.push(slot);
      if (slot === 'trainingIntent' && !normalizedSlots.trainingIntent) missingSlots.push(slot);
    });
  }

  return {
    valid: missingSlots.length === 0 && invalidSlots.length === 0,
    normalizedSlots,
    missingSlots: [...new Set(missingSlots)],
    invalidSlots
  };
};

export const buildMissingCoachTrainingSlotsMessage = (missingSlots = []) => {
  const missing = new Set(missingSlots);
  if (missing.has('trainingIntent')) {
    return 'Voce quer um treino novo, continuar sua trilha, abrir um treino, salvar, concluir ou ver progresso?';
  }
  if (missing.has('trainingId')) {
    return 'Qual treino voce quer abrir, salvar ou marcar como concluido?';
  }
  if (missing.has('painOrObjective')) {
    return 'Qual ponto voce quer treinar agora: recoil, mira, movimentacao, rush ou tomada de decisao?';
  }
  if (missing.has('gameOrProfile')) {
    return 'Qual jogo voce quer treinar agora: PUBG Mobile, Free Fire ou COD Mobile?';
  }
  return 'Preciso de mais um detalhe para escolher o treino certo da Coach.';
};

export const buildInvalidCoachTrainingSlotsMessage = (invalidSlots = []) => {
  const first = invalidSlots[0];
  if (!first) return null;

  if (first.reason === 'unsupported_game') {
    return 'Para a Coach, me diga PUBG Mobile, Free Fire ou COD Mobile. Tambem posso usar seu perfil salvo.';
  }

  if (first.reason === 'unsupported_training_intent') {
    return 'Nao entendi se voce quer treino, trilha, progresso, salvar ou concluir. Me fala com uma dessas intencoes.';
  }

  return 'Nao consegui validar esses dados para a Coach. Me passa de novo de forma simples.';
};

export default validateCoachTrainingSlots;
